import { article as articleQuery } from "@data/queries";
import { Chip, Container, Divider, Grid, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import imageUrlFor from '@utils/image';
import PostBody from '@utils/PostBody';
import sanity from '@utils/sanity';
import { NextSeo } from 'next-seo';
import { ReactElement } from 'react';
import { ArticleProps } from 'src/@types/article';
import BlogLatestPosts from 'src/sections/blog/BlogOtherPosts';
import PostHero from 'src/sections/blog/BlogPostHero';
import { Breadcrumbs, Page } from '../../src/components';
import { HEADER_DESKTOP_HEIGHT, HEADER_MOBILE_HEIGHT } from '../../src/config';
import Layout from '../../src/layouts';
import Routes from '../../src/routes';
import { BlogSidebar } from '../../src/sections/blog';
import { Newsletter } from '../../src/sections/newsletter';

// ----------------------------------------------------------------------

const articlesQuery = `*[_type == "article"] { _id, slug, title, description, header, date }`

// ----------------------------------------------------------------------

const RootStyle = styled('div')(({ theme }) => ({
  paddingTop: HEADER_MOBILE_HEIGHT,
  [theme.breakpoints.up('md')]: {
    paddingTop: HEADER_DESKTOP_HEIGHT,
  },
}));

// ----------------------------------------------------------------------

type Props = {
  article: ArticleProps;
  articles: ArticleProps[];
};

export default function PostPage({ article, articles }: Props) {
  const { title, header, description, content, related, seo, slug } = article;

  return (
    <>
      <NextSeo
        openGraph={{
          url: 'https://www.b-e-a.no' + Routes.post(slug.current),
          type: 'article',
          images: [{ url: imageUrlFor(header).url() || '' }],
          article: {
            publishedTime: article.date.toString(),
            tags: article.categories?.map((c) => c.name),
          },
        }}
      />
      <Page seo={seo}>
        <RootStyle>
          <PostHero article={article} />

          <Container>
            <Breadcrumbs
              sx={{ my: 3 }}
              links={[
                { name: 'Hjem', href: '/' },
                { name: 'Aktuelt', href: Routes.blog },
                { name: title },
              ]}
            />
          </Container>

          <Divider
            sx={{
              mb: { xs: 6, md: 10 },
            }}
          />

          <Container>
            <Grid container spacing={{ md: 8 }}>
              <Grid item xs={12} md={8}>
                <Typography variant="h5" sx={{ mb: 5 }}>
                  {description}
                </Typography>
                <Divider />

                <PostBody content={content} />

                <Stack direction="row" alignItems="center" flexWrap="wrap" sx={{ my: 6 }}>
                  <Typography variant="subtitle2" sx={{ mr: 1 }}>
                    Tags:
                  </Typography>
                  {article.categories?.map((tag) => (
                    <Chip
                      key={tag.name}
                      size="small"
                      label={tag.name}
                      sx={{ m: 0.5 }}
                      onClick={() => { }}
                    />
                  ))}
                </Stack>
              </Grid>

              <Grid item xs={12} md={4}>
                <BlogSidebar
                  recentPosts={{
                    list: related,
                    path: "/aktuelt",
                  }}
                  advertisement={{
                    title: 'Ny elbillader?',
                    description: 'Verdens smarteste elbillader til kun 13.985,- ferdig montert',
                    imageUrl:
                      'image-a0be3eac397b7d6809ea422bbcdd51a058a93276-1440x960-jpg',
                    path: '/kampanjer/elbillader/',
                  }}
                  categories={undefined}
                />
              </Grid>
            </Grid>
          </Container>

          <Divider />

          <BlogLatestPosts articles={articles} />
          <Newsletter />
        </RootStyle>
      </Page>
    </>
  );
}

// ----------------------------------------------------------------------

PostPage.getLayout = function getLayout(page: ReactElement) {
  return <Layout>{page}</Layout>;
};

// ----------------------------------------------------------------------

type Params = {
  params: {
    slug: string;
  };
};

export const getStaticPaths = async () => {
  // Get the paths we want to pre-render based on persons
  const articles = await sanity.fetch(articlesQuery);
  const paths = articles.map((article: ArticleProps) => ({
    params: { slug: article.slug.current },
  }));

  // We'll pre-render only these paths at build time.
  // { fallback: false } means other routes should 404.
  return { paths, fallback: false };
};

// This function gets called at build time on server-side.
export const getStaticProps = async ({ params }: Params) => {
  const article = await sanity.fetch(articleQuery, { slug: params.slug });
  const articles = await sanity.fetch(articlesQuery);
  return { props: { article, articles } };
};
